import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { Clock, CheckCircle2, Circle, AlertCircle } from "lucide-react";

function TimelineView({ tasks, users, onTaskClick }) {
  const statusLabels = {
    "todo": "Yapılacak",
    "in-progress": "Devam Ediyor",
    "done": "Tamamlandı"
  };

  const priorityLabels = {
    "low": "Düşük",
    "medium": "Orta",
    "high": "Yüksek"
  };

  const priorityColors = {
    "low": "bg-gray-100 text-gray-800",
    "medium": "bg-yellow-100 text-yellow-800",
    "high": "bg-red-100 text-red-800"
  };

  const statusIcons = {
    "todo": Circle,
    "in-progress": AlertCircle,
    "done": CheckCircle2
  };

  const statusColors = {
    "todo": "text-blue-500",
    "in-progress": "text-purple-500",
    "done": "text-green-500"
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || "Atanmamış";
  };

  const getUserInitials = (userId) => {
    const name = getUserName(userId);
    return name.split(" ").map(n => n[0]).join("").toUpperCase();
  };

  // Sort tasks by created_at
  const sortedTasks = [...tasks].sort((a, b) => 
    new Date(b.created_at) - new Date(a.created_at)
  );

  // Group by date
  const groupedTasks = sortedTasks.reduce((acc, task) => {
    const dateKey = format(new Date(task.created_at), "d MMMM yyyy", { locale: tr });
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(task);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {Object.keys(groupedTasks).length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          Görev bulunamadı
        </div>
      ) : (
        Object.entries(groupedTasks).map(([date, dateTasks]) => (
          <div key={date}>
            {/* Date Header */}
            <div className="flex items-center gap-4 mb-4">
              <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                {date}
              </div>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>

            {/* Tasks for this date */}
            <div className="space-y-4 ml-8">
              {dateTasks.map((task) => {
                const StatusIcon = statusIcons[task.status];
                return (
                  <div key={task.id} className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-[-32px] top-0 bottom-0 w-0.5 bg-gray-200"></div>
                    
                    {/* Timeline dot */}
                    <div className={`absolute left-[-38px] top-2 ${statusColors[task.status]}`}>
                      <StatusIcon className="h-4 w-4" fill="currentColor" />
                    </div>

                    {/* Task Card */}
                    <Card 
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => onTaskClick(task)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-lg">{task.title}</h3>
                              <Badge className={priorityColors[task.priority]}>
                                {priorityLabels[task.priority]}
                              </Badge>
                            </div>
                            
                            {task.description && (
                              <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                                {task.description}
                              </p>
                            )}

                            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                              {/* Assignee */}
                              {task.assignee_id && (
                                <div className="flex items-center gap-2">
                                  <Avatar className="h-6 w-6">
                                    <AvatarFallback className="text-xs">
                                      {getUserInitials(task.assignee_id)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span>{getUserName(task.assignee_id)}</span>
                                </div>
                              )}

                              {/* Due date */}
                              {task.due_date && (
                                <div className="flex items-center gap-1">
                                  <Clock className="h-4 w-4" />
                                  <span>
                                    {format(new Date(task.due_date), "d MMM yyyy", { locale: tr })}
                                  </span>
                                </div>
                              )}

                              {/* Status */}
                              <Badge variant="outline">
                                {statusLabels[task.status]}
                              </Badge>

                              {/* Tags */}
                              {task.tags && task.tags.length > 0 && (
                                <div className="flex gap-1 flex-wrap">
                                  {task.tags.map((tag, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs">
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Time */}
                          <div className="text-xs text-gray-400 whitespace-nowrap">
                            {format(new Date(task.created_at), "HH:mm")}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export default TimelineView;
